const desktopMedia = window.matchMedia("(min-width: 768px)");
const initializedAccordions = new WeakSet();
const initializedProductFeatures = new WeakSet();
const initializedSection4Scrolls = new WeakSet();
const initializedImagePairs = new WeakSet();
const initializedVideoShowcases = new WeakSet();
const initializedStoryLists = new WeakSet();
const svgNamespace = "http://www.w3.org/2000/svg";
const reducedMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");
// Coordinates are relative to the full-width section background.
const section4HotspotPositions = [
  { left: "54.1%", top: "11%" },
  { left: "51.8%", top: "33%" },
  { left: "53.4%", top: "66%" },
  { left: "53.2%", top: "90%" },
];
const legacySection4HotspotPositions = [
  { left: "80.13%", top: "10%" },
  { left: "77.27%", top: "32.5%" },
  { left: "81.15%", top: "63.06%" },
  { left: "78.9%", top: "85%" },
];
const section4HotspotIcon = `
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5.16667 1H1.83333C1.3731 1 1 1.3731 1 1.83333V5.16667M5.16667 16H1.83333C1.3731 16 1 15.6269 1 15.1667V11.8333M11.8333 16H15.1667C15.6269 16 16 15.6269 16 15.1667V11.8333M11.8333 1H15.1667C15.6269 1 16 1.3731 16 1.83333V5.16667M11.8333 8.5H5.16667M8.5 11.8333V5.16667" stroke="#F2F2F2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

function initializeSection4Hotspots(section, stage, cards, onSelect) {
  if (!section || !cards.length) return () => {};

  const coordinateSpace = stage || section;
  const image = stage?.querySelector("img");
  const hotspotPositions = stage
    ? legacySection4HotspotPositions
    : section4HotspotPositions;
  if (!stage) section.classList.add("is-background-feature-stage");
  const hotspots = document.createElement("div");
  hotspots.className = "wd030-section4-hotspots";
  if (!stage) hotspots.classList.add("is-background-stage");
  const connectors = document.createElementNS(svgNamespace, "svg");
  connectors.classList.add("wd030-section4-connectors");
  connectors.setAttribute("aria-hidden", "true");
  connectors.setAttribute("preserveAspectRatio", "none");

  const pairs = cards.map((card, index) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "wd030-section4-hotspot";
    button.style.left = hotspotPositions[index]?.left ?? "50%";
    button.style.top = hotspotPositions[index]?.top ?? "50%";
    button.setAttribute("aria-label", `View feature ${index + 1}`);
    button.innerHTML = section4HotspotIcon;
    for (const eventName of ["mouseenter", "focus", "click"]) {
      button.addEventListener(eventName, () => onSelect(index));
    }
    hotspots.append(button);

    const line = document.createElementNS(svgNamespace, "polyline");
    line.classList.add("wd030-section4-connector");
    connectors.append(line);
    return { button, card, line };
  });

  coordinateSpace.append(hotspots);
  section.append(connectors);

  let frame = 0;
  const update = () => {
    frame = 0;
    if (!desktopMedia.matches || !section.isConnected) return;
    const bounds = section.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    if (stage && image) {
      const stageRect = stage.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      Object.assign(hotspots.style, {
        left: `${imageRect.left - stageRect.left}px`,
        top: `${imageRect.top - stageRect.top}px`,
        width: `${imageRect.width}px`,
        height: `${imageRect.height}px`,
      });
    } else if (stage) {
      const stageRect = stage.getBoundingClientRect();
      Object.assign(hotspots.style, {
        left: "0px",
        top: "0px",
        width: `${stageRect.width}px`,
        height: `${stageRect.height}px`,
      });
    } else if (!stage) {
      Object.assign(hotspots.style, {
        left: "0px",
        top: "0px",
        width: "100%",
        height: "100%",
      });
    }

    connectors.setAttribute("viewBox", `0 0 ${bounds.width} ${bounds.height}`);
    pairs.forEach(({ button, card, line }) => {
      const source = button.getBoundingClientRect();
      const target = card.getBoundingClientRect();
      const x1 = source.left + source.width / 2 - bounds.left;
      const y1 = source.top + source.height / 2 - bounds.top;
      const x2 = target.left - bounds.left + 6;
      const y2 = target.top + target.height / 2 - bounds.top;
      const bend = x2 - Math.min(64, Math.max(0, (x2 - x1) / 3));
      line.setAttribute("points", `${x1},${y1} ${bend},${y2} ${x2},${y2}`);
    });
  };
  const scheduleUpdate = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };
  const observer = window.ResizeObserver
    ? new window.ResizeObserver(scheduleUpdate)
    : null;
  [section, stage, image, cards[0]?.parentElement, ...cards]
    .filter(Boolean)
    .forEach((node) => observer?.observe(node));
  window.addEventListener("resize", scheduleUpdate, { passive: true });
  image?.addEventListener("load", scheduleUpdate);
  document.fonts?.ready.then(scheduleUpdate);
  scheduleUpdate();

  return (index) => {
    pairs.forEach(({ button, line }, pairIndex) => {
      button.classList.toggle("is-active", index === pairIndex);
      line.classList.toggle("is-active", index === pairIndex);
    });
    scheduleUpdate();
  };
}

function createGuideLine(section) {
  const svg = document.createElementNS(svgNamespace, "svg");
  const path = document.createElementNS(svgNamespace, "path");
  const endpoint = document.createElementNS(svgNamespace, "circle");

  svg.classList.add("wd030-guide-line");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("preserveAspectRatio", "none");
  endpoint.setAttribute("r", "4");
  svg.append(path, endpoint);
  section.append(svg);

  return { svg, path, endpoint };
}

function initializeAccordion(section) {
  const stage = section.querySelector(':scope > [handle="container1"]');
  const accordion = section.querySelector(':scope > [handle="container2"]');

  if (!accordion || initializedAccordions.has(accordion)) return;

  const panels = stage
    ? Array.from(stage.children).filter(
        (child) => child.getAttribute("handle") === "container",
      )
    : [];
  const items = Array.from(accordion.children).filter(
    (child) => child.getAttribute("handle") === "container",
  );

  if (!items.length) return;

  const guide = createGuideLine(section);
  let activeIndex = -1;
  let guideFrame = 0;
  let guideTimer = 0;
  let guideShouldReveal = false;
  let guideShouldAnimate = false;

  const cancelGuideAnimation = () => {
    guide.path.getAnimations().forEach((animation) => animation.cancel());
    guide.endpoint.getAnimations().forEach((animation) => animation.cancel());
  };

  const drawGuideLine = () => {
    cancelGuideAnimation();

    if (reducedMotionMedia.matches) {
      guide.path.style.strokeDasharray = "none";
      guide.path.style.strokeDashoffset = "0";
      guide.endpoint.style.opacity = "1";
      return;
    }

    const pathLength = guide.path.getTotalLength();

    guide.path.style.strokeDasharray = `${pathLength}`;
    guide.path.style.strokeDashoffset = `${pathLength}`;
    guide.endpoint.style.opacity = "0";
    guide.path.animate(
      [
        { strokeDashoffset: pathLength },
        { strokeDashoffset: 0 },
      ],
      {
        duration: 480,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "forwards",
      },
    );
    guide.endpoint.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: 0, offset: 0.82 },
        { opacity: 1, offset: 1 },
      ],
      { duration: 520, easing: "ease-out", fill: "forwards" },
    );
  };

  const updateGuideLine = () => {
    guideFrame = 0;

    if (!desktopMedia.matches || !panels[activeIndex] || !items[activeIndex]) {
      guide.svg.classList.remove("is-visible");
      return;
    }

    const source = panels[activeIndex].querySelector('[handle="richtext"]');
    const target = items[activeIndex].querySelector('[handle="text"]');

    if (!source || !target) {
      guide.svg.classList.remove("is-visible");
      return;
    }

    const sectionRect = section.getBoundingClientRect();
    const sourceRect = source.getBoundingClientRect();
    const itemRect = items[activeIndex].getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const sourceOffset = Math.max(
      24,
      Math.min(48, sectionRect.width * 0.025),
    );
    const targetGap = Math.max(
      12,
      Math.min(24, sectionRect.width * 0.0125),
    );
    const startX = sourceRect.right - sectionRect.left + sourceOffset;
    const startY = sourceRect.bottom - sectionRect.top;
    const endX = itemRect.left - sectionRect.left - targetGap;
    const endY = targetRect.top + targetRect.height / 2 - sectionRect.top;
    const horizontalSpace = endX - startX;

    if (
      sectionRect.width <= 0 ||
      sectionRect.height <= 0 ||
      horizontalSpace < 48
    ) {
      guide.svg.classList.remove("is-visible");
      return;
    }

    const bendX = startX + horizontalSpace * 0.55;

    guide.svg.setAttribute(
      "viewBox",
      `0 0 ${sectionRect.width} ${sectionRect.height}`,
    );
    guide.path.setAttribute(
      "d",
      `M ${startX} ${startY} H ${bendX} L ${endX} ${endY}`,
    );
    guide.endpoint.setAttribute("cx", String(endX));
    guide.endpoint.setAttribute("cy", String(endY));
    guide.svg.classList.toggle("is-visible", guideShouldReveal);

    if (guideShouldReveal && guideShouldAnimate) {
      guideShouldAnimate = false;
      drawGuideLine();
    }
  };

  const scheduleGuideUpdate = () => {
    if (guideFrame) return;
    guideFrame = window.requestAnimationFrame(updateGuideLine);
  };

  const scheduleSettledGuideUpdate = () => {
    window.clearTimeout(guideTimer);
    guideTimer = window.setTimeout(() => {
      guideShouldReveal = true;
      guideShouldAnimate = true;
      scheduleGuideUpdate();
    }, 240);
  };

  const updateCardGeometry = () => {
    if (activeIndex < 0) return;

    const accordionStyle = window.getComputedStyle(accordion);
    const parsedGap = Number.parseFloat(accordionStyle.rowGap);
    const gap = Number.isFinite(parsedGap) ? parsedGap : 0;
    const availableHeight = accordion.clientHeight - gap * (items.length - 1);
    const collapsedHeight = Math.max(
      0,
      availableHeight / (items.length + 3),
    );
    const expandedHeight = collapsedHeight * 4;
    const collapsedClip = (expandedHeight - collapsedHeight) / 2;
    let slotY = 0;

    items.forEach((item, index) => {
      const isActive = index === activeIndex;
      const clip = isActive ? 0 : collapsedClip;

      item.style.setProperty(
        "--wd030-card-height",
        `${expandedHeight.toFixed(3)}px`,
      );
      item.style.setProperty(
        "--wd030-card-clip",
        `${clip.toFixed(3)}px`,
      );
      item.style.setProperty(
        "--wd030-card-y",
        `${(slotY - clip).toFixed(3)}px`,
      );

      slotY += (isActive ? expandedHeight : collapsedHeight) + gap;
    });
  };

  const setActiveItem = (nextIndex) => {
    const normalizedIndex = Math.min(nextIndex, items.length - 1);

    if (normalizedIndex === activeIndex) return;

    activeIndex = normalizedIndex;
    guideShouldReveal = false;
    guideShouldAnimate = false;
    cancelGuideAnimation();
    guide.svg.classList.remove("is-visible");
    updateCardGeometry();

    items.forEach((item, index) => {
      const isActive = index === activeIndex;
      item.classList.toggle("is-active", isActive);
    });

    panels.forEach((panel, index) => {
      const isActive = index === activeIndex;
      panel.classList.toggle("is-active", isActive);
      panel.setAttribute("aria-hidden", String(!isActive));
    });

    scheduleGuideUpdate();
    scheduleSettledGuideUpdate();
  };

  const syncResponsiveState = () => {
    if (desktopMedia.matches) {
      stage?.classList.toggle("is-enhanced", panels.length > 0);
      accordion.classList.add("is-enhanced", "is-positioning");
      setActiveItem(0);
      window.requestAnimationFrame(() => {
        accordion.classList.remove("is-positioning");
      });
      return;
    }

    items.forEach((item) => {
      item.classList.remove("is-active");
    });
    panels.forEach((panel) => {
      panel.classList.remove("is-active");
      panel.removeAttribute("aria-hidden");
    });
    stage?.classList.remove("is-enhanced");
    accordion.classList.remove("is-enhanced", "is-positioning");
    window.clearTimeout(guideTimer);
    guide.svg.classList.remove("is-visible");
    guideShouldReveal = false;
    guideShouldAnimate = false;
    cancelGuideAnimation();
    activeIndex = -1;
  };

  items.forEach((item, index) => {
    item.addEventListener("mouseenter", () => {
      if (desktopMedia.matches) setActiveItem(index);
    });
  });

  accordion.addEventListener("transitionend", (event) => {
    if (
      event.propertyName !== "transform" ||
      event.target !== items[activeIndex]
    ) {
      return;
    }

    window.clearTimeout(guideTimer);
    guideShouldReveal = true;
    guideShouldAnimate = true;
    scheduleGuideUpdate();
  });

  const resizeObserver = window.ResizeObserver
    ? new window.ResizeObserver(() => {
        if (desktopMedia.matches && activeIndex >= 0) {
          accordion.classList.add("is-positioning");
          updateCardGeometry();
          window.requestAnimationFrame(() => {
            accordion.classList.remove("is-positioning");
          });
        }

        scheduleGuideUpdate();
      })
    : null;

  [section, stage, accordion].filter(Boolean).forEach((element) => {
    resizeObserver?.observe(element);
  });
  if (!resizeObserver) {
    window.addEventListener("resize", scheduleGuideUpdate, { passive: true });
  }
  document.fonts?.ready.then(scheduleGuideUpdate);

  desktopMedia.addEventListener("change", syncResponsiveState);
  syncResponsiveState();
  initializedAccordions.add(accordion);
}

function initializeSection3(root = document) {
  const sections = root.matches?.('[handle="section3"]')
    ? [root]
    : root.querySelectorAll('[handle="section3"]');

  sections.forEach(initializeAccordion);
}

function initializeProductFeature(section) {
  const list = section.querySelector(':scope > [handle="container2"]');
  if (!list || initializedProductFeatures.has(list)) return;

  const cards = Array.from(list.children).filter(
    (child) => child.getAttribute("handle") === "container",
  );
  if (!cards.length) return;

  const stage = Array.from(section.children).find((child) =>
    ["image", "container1"].includes(child.getAttribute("handle")?.toLowerCase()),
  );
  let activeIndex = -1;
  const syncHotspots = initializeSection4Hotspots(
    section,
    stage,
    cards,
    (index) => selectCard(index),
  );
  const selectCard = (index) => {
    if (!desktopMedia.matches || activeIndex === index) return;

    activeIndex = index;
    list.classList.add("is-enhanced");
    cards.forEach((card, cardIndex) => {
      const isActive = cardIndex === index;
      card.classList.toggle("is-active", isActive);
      card.setAttribute("aria-expanded", String(isActive));
    });
    syncHotspots(index);
  };

  cards.forEach((card, index) => {
    card.setAttribute("role", "button");
    card.setAttribute("tabindex", "0");
    card.addEventListener("mouseenter", () => selectCard(index));
    card.addEventListener("focus", () => selectCard(index));
    card.addEventListener("click", () => selectCard(index));
    card.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      selectCard(index);
    });
  });

  const syncResponsiveState = () => {
    if (desktopMedia.matches) {
      activeIndex = -1;
      selectCard(0);
    } else {
      activeIndex = -1;
      list.classList.remove("is-enhanced");
      syncHotspots(-1);
      cards.forEach((card) => {
        card.classList.remove("is-active");
        card.removeAttribute("aria-expanded");
      });
    }
  };

  desktopMedia.addEventListener("change", syncResponsiveState);
  syncResponsiveState();
  initializedProductFeatures.add(list);
}

function initializeSection4Scroll(section) {
  const embla = section.querySelector(':scope > [handle="embla"][data-embla]');
  const viewport = embla?.querySelector("[data-embla-viewport]");
  const track = embla?.querySelector("[data-embla-container]");
  const slides = track
    ? Array.from(track.children).filter((child) =>
        child.hasAttribute("data-embla-slide"),
      )
    : [];
  if (
    !embla ||
    !viewport ||
    slides.length < 2 ||
    initializedSection4Scrolls.has(section)
  ) {
    return;
  }

  let emblaApi = null;
  let scrollFrame = 0;
  let layoutFrame = 0;
  let pinFrame = 0;
  let scrollStep = 1;
  let stickyTop = -1;
  let viewportHeight = -1;
  let sectionHeight = -1;
  let activeIndex = 0;
  let dots = [];
  let touchStartY = 0;
  let touchStartedAtTop = false;
  let touchStartedAtBottom = false;
  let pageTouchLastY = 0;
  let pageTouchActive = false;
  let pageTouchStartedPinned = false;
  let touchHandoffEnabled = false;
  let touchHandoffActive = false;
  let touchHandoffDistance = 0;
  let touchHandoffStartIndex = 0;
  let lastPageScrollY = window.scrollY;
  let pinnedPageScrollY = window.scrollY;
  let pinnedPageResetTimer = 0;
  let isPinned = false;
  let pinPlaceholder = null;
  const controller = new AbortController();
  embla.dataset.emblaDisableDrag = "true";

  const isAtBottom = () =>
    viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop <= 1;

  const syncStickyTop = (forceHeight = false) => {
    const headerBottom = document
      .querySelector("#pageheader")
      ?.getBoundingClientRect().bottom;
    const fallback = Number.isFinite(headerBottom)
      ? 0
      : Number.parseFloat(
          window
            .getComputedStyle(document.documentElement)
            .getPropertyValue("--theme-sticky-header-height"),
        ) || 0;
    const nextViewportHeight =
      window.visualViewport?.height || window.innerHeight;
    const nextStickyTop = Math.min(
      nextViewportHeight,
      Math.max(0, Number.isFinite(headerBottom) ? headerBottom : fallback),
    );
    const nextSectionHeight = Math.max(
      1,
      nextViewportHeight - nextStickyTop,
    );
    const shouldSyncHeight =
      (!isPinned || forceHeight) &&
      Math.abs(nextSectionHeight - sectionHeight) >= 0.5;
    if (
      Math.abs(nextStickyTop - stickyTop) < 0.5 &&
      Math.abs(nextViewportHeight - viewportHeight) < 0.5 &&
      !shouldSyncHeight
    ) {
      return false;
    }

    stickyTop = nextStickyTop;
    viewportHeight = nextViewportHeight;
    section.style.setProperty(
      "--wd030-section4-sticky-top",
      `${nextStickyTop}px`,
    );
    if (shouldSyncHeight) {
      sectionHeight = nextSectionHeight;
      section.style.setProperty(
        "--wd030-section4-height",
        `${nextSectionHeight}px`,
      );
    }
    return true;
  };

  const refreshInteractiveLayout = () => {
    if (!syncStickyTop(true)) return;

    if (pinPlaceholder) {
      pinPlaceholder.style.height = `${section.getBoundingClientRect().height}px`;
    }
    scrollStep = Math.max(
      1,
      viewport.clientHeight || embla.getBoundingClientRect().height,
    );
    viewport.scrollTo({
      top: activeIndex * scrollStep,
      behavior: "auto",
    });
    scheduleScrollSync();
  };

  const cancelPinnedPageReset = () => {
    window.clearTimeout(pinnedPageResetTimer);
    pinnedPageResetTimer = 0;
  };

  const resetPinnedPagePosition = () => {
    cancelPinnedPageReset();
    if (!isPinned || desktopMedia.matches || !section.isConnected) return;
    if (Math.abs(window.scrollY - pinnedPageScrollY) <= 1) return;

    window.scrollTo({ top: pinnedPageScrollY, behavior: "auto" });
    lastPageScrollY = pinnedPageScrollY;
  };

  const schedulePinnedPageReset = () => {
    cancelPinnedPageReset();
    pinnedPageResetTimer = window.setTimeout(resetPinnedPagePosition, 120);
  };

  function transferTouchToViewport(event) {
    if (!pageTouchActive) return;

    const currentY = event.touches[0]?.clientY ?? pageTouchLastY;
    const deltaY = pageTouchLastY - currentY;
    pageTouchLastY = currentY;
    if (!isPinned || pageTouchStartedPinned || !deltaY) return;

    if (!touchHandoffActive) {
      touchHandoffActive = true;
      touchHandoffDistance = 0;
      touchHandoffStartIndex = Math.round(viewport.scrollTop / scrollStep);
      viewport.classList.add("is-touch-handoff");
    }

    if (event.cancelable) event.preventDefault();
    touchHandoffDistance += deltaY;
    viewport.scrollTop = Math.max(
      0,
      Math.min(
        viewport.scrollHeight - viewport.clientHeight,
        viewport.scrollTop + deltaY,
      ),
    );
    scheduleScrollSync();
  }

  const enableTouchHandoff = () => {
    if (touchHandoffEnabled) return;

    touchHandoffEnabled = true;
    window.addEventListener("touchmove", transferTouchToViewport, {
      passive: false,
      signal: controller.signal,
    });
  };

  const disableTouchHandoff = () => {
    if (!touchHandoffEnabled) return;

    window.removeEventListener("touchmove", transferTouchToViewport);
    touchHandoffEnabled = false;
  };

  const beginPageTouch = (event) => {
    pageTouchActive = true;
    pageTouchStartedPinned = isPinned;
    pageTouchLastY = event.touches[0]?.clientY ?? 0;
    touchHandoffActive = false;
    touchHandoffDistance = 0;

    if (desktopMedia.matches || isPinned) return;
    const bounds = section.getBoundingClientRect();
    const currentViewportHeight =
      window.visualViewport?.height || window.innerHeight;
    const isApproachingSection =
      bounds.top <= currentViewportHeight * 2 &&
      bounds.bottom >= -currentViewportHeight;
    if (isApproachingSection) enableTouchHandoff();
  };

  const finishPageTouch = () => {
    if (touchHandoffActive && isPinned) {
      const movedToNextSlide = Math.abs(touchHandoffDistance) >= 12;
      const nextIndex = movedToNextSlide
        ? touchHandoffStartIndex + Math.sign(touchHandoffDistance)
        : Math.round(viewport.scrollTop / scrollStep);
      const targetIndex = Math.max(
        0,
        Math.min(slides.length - 1, nextIndex),
      );

      viewport.classList.remove("is-touch-handoff");
      viewport.scrollTo({
        top: targetIndex * scrollStep,
        behavior: reducedMotionMedia.matches ? "auto" : "smooth",
      });
      setActiveSlide(targetIndex);
    } else {
      viewport.classList.remove("is-touch-handoff");
    }

    pageTouchActive = false;
    touchHandoffActive = false;
    touchHandoffDistance = 0;
    disableTouchHandoff();
  };

  const setPinned = (shouldPin) => {
    const nextPinned = shouldPin && !desktopMedia.matches;
    if (nextPinned === isPinned) return;

    if (nextPinned) {
      pinPlaceholder = document.createElement("div");
      pinPlaceholder.className = "wd030-section4-pin-placeholder";
      pinPlaceholder.setAttribute("aria-hidden", "true");
      pinPlaceholder.style.height = `${section.getBoundingClientRect().height}px`;
      section.before(pinPlaceholder);
      section.classList.add("is-pinned-scroll-carousel");
      pinnedPageScrollY = window.scrollY;
      isPinned = true;
      if (pageTouchActive && !pageTouchStartedPinned) enableTouchHandoff();
      return;
    }

    section.classList.remove("is-pinned-scroll-carousel");
    pinPlaceholder?.remove();
    pinPlaceholder = null;
    cancelPinnedPageReset();
    isPinned = false;
    lastPageScrollY = window.scrollY;
    sectionHeight = -1;
    if (!desktopMedia.matches) syncStickyTop();
  };

  const beginTouch = (event) => {
    refreshInteractiveLayout();
    touchStartY = event.touches[0]?.clientY ?? 0;
    touchStartedAtTop = viewport.scrollTop <= 1;
    touchStartedAtBottom = isAtBottom();
  };

  const updateTouchChain = (event) => {
    if (!isPinned) return;

    const currentY = event.touches[0]?.clientY ?? touchStartY;
    const delta = currentY - touchStartY;
    const shouldReleaseToPage =
      (touchStartedAtTop && viewport.scrollTop <= 1 && delta > 4) ||
      (touchStartedAtBottom && isAtBottom() && delta < -4);
    if (!shouldReleaseToPage) return;

    setPinned(false);
  };

  const releaseWheelToPage = (event) => {
    if (!isPinned) return;

    const shouldReleaseToPage =
      (event.deltaY < 0 && viewport.scrollTop <= 1) ||
      (event.deltaY > 0 && isAtBottom());
    if (!shouldReleaseToPage) return;

    setPinned(false);
    window.scrollBy({
      top: event.deltaY,
      behavior: "auto",
    });
  };

  const setActiveSlide = (index) => {
    activeIndex = Math.max(0, Math.min(slides.length - 1, index));
    slides.forEach((slide, slideIndex) => {
      slide.setAttribute("aria-hidden", String(slideIndex !== activeIndex));
    });
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === activeIndex;
      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-current", isActive ? "true" : "false");
    });
  };

  const mountDots = () => {
    const dotsRoot = embla.querySelector("[data-embla-dots]");
    if (!dotsRoot) return;
    if (
      dots.length === slides.length &&
      dots.every((dot) => dot.parentElement === dotsRoot)
    ) {
      setActiveSlide(activeIndex);
      return;
    }

    dots = slides.map((_, index) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "wd030-section4-scroll-dot";
      dot.setAttribute("aria-label", `View feature ${index + 1}`);
      dot.addEventListener("click", () => {
        refreshInteractiveLayout();
        viewport.scrollTo({
          top: index * scrollStep,
          behavior: reducedMotionMedia.matches ? "auto" : "smooth",
        });
      });
      return dot;
    });
    dotsRoot.replaceChildren(...dots);
    setActiveSlide(activeIndex);
  };

  const syncToViewportScroll = () => {
    scrollFrame = 0;
    if (desktopMedia.matches || !section.isConnected) return;
    const nextIndex = Math.round(viewport.scrollTop / scrollStep);
    if (nextIndex !== activeIndex) setActiveSlide(nextIndex);
  };

  const scheduleScrollSync = () => {
    if (!scrollFrame) {
      scrollFrame = window.requestAnimationFrame(syncToViewportScroll);
    }
  };

  const syncSlideLayout = () => {
    layoutFrame = 0;
    if (desktopMedia.matches || !section.isConnected) return;

    if (pinPlaceholder) {
      pinPlaceholder.style.height = `${section.getBoundingClientRect().height}px`;
    }
    scrollStep = Math.max(
      1,
      viewport.clientHeight || embla.getBoundingClientRect().height,
    );
    viewport.scrollTo({
      top: activeIndex * scrollStep,
      behavior: "auto",
    });
    scheduleScrollSync();
  };

  const scheduleSlideLayout = () => {
    if (!layoutFrame) {
      layoutFrame = window.requestAnimationFrame(syncSlideLayout);
    }
  };

  const syncPinnedPosition = () => {
    pinFrame = 0;
    const currentPageScrollY = window.scrollY;
    if (desktopMedia.matches || !section.isConnected) {
      setPinned(false);
      lastPageScrollY = currentPageScrollY;
      return;
    }

    if (isPinned) {
      if (Math.abs(currentPageScrollY - pinnedPageScrollY) > 1) {
        schedulePinnedPageReset();
      }
      lastPageScrollY = currentPageScrollY;
      return;
    }

    if (syncStickyTop()) scheduleSlideLayout();

    const sectionBounds = section.getBoundingClientRect();
    const direction = currentPageScrollY - lastPageScrollY;
    const canScrollInside =
      direction < 0 ? viewport.scrollTop > 1 : !isAtBottom();
    const reachedStickyTop =
      sectionBounds.top <= stickyTop + 1 &&
      sectionBounds.bottom > stickyTop + 1;

    if (canScrollInside && reachedStickyTop) {
      const correction = sectionBounds.top - stickyTop;
      if (Math.abs(correction) > 1) {
        window.scrollBy({ top: correction, behavior: "auto" });
      }
      setPinned(true);
    }
    lastPageScrollY = window.scrollY;
  };

  const schedulePinSync = () => {
    if (!pinFrame) {
      pinFrame = window.requestAnimationFrame(syncPinnedPosition);
    }
  };

  const syncLayout = () => {
    if (desktopMedia.matches) {
      setPinned(false);
      section.classList.remove("is-scroll-carousel");
      section.style.removeProperty("--wd030-section4-sticky-top");
      section.style.removeProperty("--wd030-section4-height");
      stickyTop = -1;
      viewportHeight = -1;
      sectionHeight = -1;
      slides.forEach((slide) => slide.removeAttribute("aria-hidden"));
      return;
    }

    syncStickyTop();
    section.classList.add("is-scroll-carousel");
    connectEmbla();
    if (pinPlaceholder) {
      pinPlaceholder.style.height = `${section.getBoundingClientRect().height}px`;
    }
    scheduleSlideLayout();
    schedulePinSync();
  };

  const handleViewportResize = () => {
    if (isPinned) return;
    syncLayout();
  };

  const connectEmbla = () => {
    if (desktopMedia.matches) return;

    const api = embla.__tideweEmblaApi;
    if (api && api !== emblaApi) {
      emblaApi = api;
      api.destroy?.();

      const instances = window.theme?.EmblasInstances;
      if (Array.isArray(instances)) {
        const index = instances.indexOf(api);
        if (index >= 0) instances.splice(index, 1);
      }
      delete embla.__tideweEmblaApi;
    }

    mountDots();
    scheduleScrollSync();
  };

  const observer = new MutationObserver(connectEmbla);
  observer.observe(embla, {
    attributes: true,
    attributeFilter: ["data-embla-initialized"],
  });
  connectEmbla();

  const cleanup = (event) => {
    if (!event.target.contains(section)) return;
    controller.abort();
    observer.disconnect();
    window.cancelAnimationFrame(scrollFrame);
    window.cancelAnimationFrame(layoutFrame);
    window.cancelAnimationFrame(pinFrame);
    cancelPinnedPageReset();
    finishPageTouch();
    setPinned(false);
    section.style.removeProperty("--wd030-section4-sticky-top");
    section.style.removeProperty("--wd030-section4-height");
    desktopMedia.removeEventListener("change", syncLayout);
    initializedSection4Scrolls.delete(section);
  };

  viewport.addEventListener("scroll", scheduleScrollSync, {
    passive: true,
    signal: controller.signal,
  });
  viewport.addEventListener("touchstart", beginTouch, {
    passive: true,
    signal: controller.signal,
  });
  viewport.addEventListener("touchmove", updateTouchChain, {
    passive: true,
    signal: controller.signal,
  });
  viewport.addEventListener("wheel", releaseWheelToPage, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("touchstart", beginPageTouch, {
    passive: false,
    signal: controller.signal,
  });
  window.addEventListener("touchend", finishPageTouch, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("touchcancel", finishPageTouch, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("scroll", schedulePinSync, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("scrollend", resetPinnedPagePosition, {
    passive: true,
    signal: controller.signal,
  });
  window.addEventListener("resize", handleViewportResize, {
    passive: true,
    signal: controller.signal,
  });
  window.visualViewport?.addEventListener("resize", handleViewportResize, {
    passive: true,
    signal: controller.signal,
  });
  document.addEventListener("shopify:section:unload", cleanup, {
    signal: controller.signal,
  });
  desktopMedia.addEventListener("change", syncLayout);
  syncLayout();
  initializedSection4Scrolls.add(section);
}

function initializeSection4(root = document) {
  const sections = root.matches?.('[handle="section4"]')
    ? [root]
    : root.querySelectorAll('[handle="section4"]');

  sections.forEach((section) => {
    initializeProductFeature(section);
    initializeSection4Scroll(section);
  });
}

function initializeSection7Pair(section) {
  const grid = section.querySelector(':scope > [handle="container1"]');
  const preview = section.querySelector(':scope > [handle="container2"]');
  if (!grid || !preview || initializedImagePairs.has(section)) return;

  const directImages = (container) =>
    Array.from(container.children).filter(
      (child) => child.getAttribute("handle")?.toLowerCase() === "image",
    );
  const thumbnails = directImages(grid);
  const previews = directImages(preview);
  const count = Math.min(thumbnails.length, previews.length);
  if (!count) return;

  const order = [0, 1, 2, 5, 4, 3].filter((index) => index < count);
  for (let index = 0; index < count; index++) {
    if (!order.includes(index)) order.push(index);
  }
  let activeIndex = 0;
  let timer = 0;
  let hoverPaused = false;
  let focusPaused = false;

  const stopAutoplay = () => {
    window.clearTimeout(timer);
    timer = 0;
  };
  const scheduleAutoplay = () => {
    stopAutoplay();
    if (
      !desktopMedia.matches ||
      reducedMotionMedia.matches ||
      document.hidden ||
      hoverPaused ||
      focusPaused ||
      order.length < 2
    ) return;
    timer = window.setTimeout(() => {
      if (!section.isConnected) return;
      selectImage(order[(order.indexOf(activeIndex) + 1) % order.length]);
      scheduleAutoplay();
    }, 3000);
  };
  const selectImage = (index) => {
    if (!desktopMedia.matches) return;
    activeIndex = index;
    thumbnails.forEach((thumbnail, imageIndex) => {
      const active = imageIndex === index;
      thumbnail.classList.toggle("is-active", active);
      thumbnail.setAttribute("aria-selected", String(active));
      thumbnail.setAttribute("tabindex", active ? "0" : "-1");
    });
    previews.forEach((image, imageIndex) => {
      const active = imageIndex === index;
      image.classList.toggle("is-active", active);
      image.setAttribute("aria-hidden", String(!active));
    });
  };
  const syncResponsiveState = () => {
    if (desktopMedia.matches) {
      grid.classList.add("is-enhanced");
      grid.setAttribute("role", "tablist");
      preview.classList.add("is-enhanced");
      thumbnails.forEach((image) => image.setAttribute("role", "tab"));
      previews.forEach((image) => image.setAttribute("role", "tabpanel"));
      selectImage(activeIndex);
    } else {
      hoverPaused = false;
      focusPaused = false;
      grid.classList.remove("is-enhanced");
      grid.removeAttribute("role");
      preview.classList.remove("is-enhanced");
      thumbnails.forEach((image) => {
        image.classList.remove("is-active");
        image.removeAttribute("aria-selected");
        image.removeAttribute("tabindex");
        image.removeAttribute("role");
      });
      previews.forEach((image) => {
        image.classList.remove("is-active");
        image.removeAttribute("aria-hidden");
        image.removeAttribute("role");
      });
    }
    scheduleAutoplay();
  };

  thumbnails.forEach((thumbnail, index) => {
    thumbnail.setAttribute("role", "tab");
    thumbnail.addEventListener("mouseenter", () => {
      if (!desktopMedia.matches) return;
      hoverPaused = true;
      stopAutoplay();
      selectImage(index);
    });
    thumbnail.addEventListener("mouseleave", () => {
      hoverPaused = false;
      scheduleAutoplay();
    });
    thumbnail.addEventListener("focusin", () => {
      if (!desktopMedia.matches) return;
      focusPaused = true;
      stopAutoplay();
      selectImage(index);
    });
    thumbnail.addEventListener("click", () => {
      selectImage(index);
      scheduleAutoplay();
    });
    thumbnail.addEventListener("keydown", (event) => {
      if (!desktopMedia.matches) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        selectImage(index);
      } else if (["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        const step = ["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1;
        thumbnails[(index + step + count) % count].focus();
      }
    });
  });
  grid.addEventListener("pointerleave", () => {
    hoverPaused = false;
    scheduleAutoplay();
  });
  grid.addEventListener("focusout", (event) => {
    if (grid.contains(event.relatedTarget)) return;
    focusPaused = false;
    scheduleAutoplay();
  });
  desktopMedia.addEventListener("change", syncResponsiveState);
  reducedMotionMedia.addEventListener("change", scheduleAutoplay);
  document.addEventListener("visibilitychange", scheduleAutoplay);
  syncResponsiveState();
  initializedImagePairs.add(section);
}

function initializeSection7(root = document) {
  const sections = root.matches?.('[handle="section7"]')
    ? [root]
    : root.querySelectorAll('[handle="section7"]');

  sections.forEach(initializeSection7Pair);
}

function initializeVideoShowcase(section) {
  const track = Array.from(section.children).find(
    (child) =>
      child.getAttribute("handle") === "container" &&
      child.querySelector(':scope > [handle="container-video"]'),
  );

  if (!track || initializedVideoShowcases.has(track)) return;

  const cards = Array.from(track.children).filter(
    (child) => child.getAttribute("handle") === "container-video",
  );

  if (!cards.length) return;

  const autoplayDelay = 5000;
  let activeIndex = 0;
  let autoplayTimer = 0;
  let scrollFrame = 0;
  let scrollEndTimer = 0;
  let pointerIsDown = false;

  const clearAutoplay = () => {
    window.clearTimeout(autoplayTimer);
    autoplayTimer = 0;
  };

  const syncCards = () => {
    const isDesktop = desktopMedia.matches;

    track.classList.toggle("is-video-enhanced", isDesktop);
    track.classList.toggle("is-mobile-carousel", !isDesktop);
    cards.forEach((card, index) => {
      const isActive = index === activeIndex;
      const video = card.querySelector(":scope > .container-bg-video");

      card.classList.toggle("is-active", isActive);
      if (isDesktop) {
        card.removeAttribute("aria-hidden");
      } else {
        card.setAttribute("aria-hidden", String(!isActive));
      }

      if (!video) return;

      if (isActive) {
        try {
          const playPromise = video.play();
          playPromise?.catch(() => {});
        } catch {}
      } else {
        video.pause();
      }
    });
  };

  const getCardScrollLeft = (card) => {
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    return track.scrollLeft + cardRect.left - trackRect.left;
  };

  const scrollToCard = (nextIndex, behavior = "smooth") => {
    activeIndex = (nextIndex + cards.length) % cards.length;
    syncCards();
    track.scrollTo({
      left: getCardScrollLeft(cards[activeIndex]),
      behavior,
    });
  };

  const scheduleAutoplay = () => {
    clearAutoplay();

    if (
      desktopMedia.matches ||
      cards.length < 2 ||
      document.hidden ||
      reducedMotionMedia.matches
    ) {
      return;
    }

    autoplayTimer = window.setTimeout(() => {
      scrollToCard(activeIndex + 1);
      scheduleAutoplay();
    }, autoplayDelay);
  };

  const syncActiveCardFromScroll = () => {
    scrollFrame = 0;
    if (desktopMedia.matches) return;

    const trackLeft = track.getBoundingClientRect().left;
    let nearestIndex = activeIndex;
    let nearestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
      if (distance >= nearestDistance) return;

      nearestDistance = distance;
      nearestIndex = index;
    });

    if (nearestIndex === activeIndex) return;

    activeIndex = nearestIndex;
    syncCards();
  };

  const scheduleScrollSync = () => {
    if (!scrollFrame) {
      scrollFrame = window.requestAnimationFrame(syncActiveCardFromScroll);
    }

    window.clearTimeout(scrollEndTimer);
    scrollEndTimer = window.setTimeout(() => {
      syncActiveCardFromScroll();
      if (!pointerIsDown) scheduleAutoplay();
    }, 180);
  };

  const syncResponsiveState = () => {
    clearAutoplay();
    window.clearTimeout(scrollEndTimer);

    if (desktopMedia.matches) {
      track.scrollLeft = 0;
      syncCards();
      return;
    }

    syncCards();
    window.requestAnimationFrame(() => {
      scrollToCard(activeIndex, "auto");
    });
    scheduleAutoplay();
  };

  cards.forEach((card, index) => {
    card.addEventListener("mouseenter", () => {
      if (!desktopMedia.matches || index === activeIndex) return;

      activeIndex = index;
      syncCards();
    });
  });

  track.addEventListener("scroll", scheduleScrollSync, { passive: true });
  track.addEventListener(
    "pointerdown",
    () => {
      pointerIsDown = true;
      clearAutoplay();
    },
    { passive: true },
  );
  ["pointerup", "pointercancel"].forEach((eventName) => {
    track.addEventListener(
      eventName,
      () => {
        pointerIsDown = false;
        syncActiveCardFromScroll();
        scheduleAutoplay();
      },
      { passive: true },
    );
  });
  track.addEventListener("focusin", clearAutoplay);
  track.addEventListener("focusout", scheduleAutoplay);
  document.addEventListener("visibilitychange", scheduleAutoplay);
  reducedMotionMedia.addEventListener("change", scheduleAutoplay);

  const videoObserver = new MutationObserver(syncResponsiveState);
  videoObserver.observe(track, { childList: true, subtree: true });
  desktopMedia.addEventListener("change", syncResponsiveState);

  syncResponsiveState();
  initializedVideoShowcases.add(track);
}

function initializeSection5(root = document) {
  const sections = root.matches?.('[handle="section5"]')
    ? [root]
    : root.querySelectorAll('[handle="section5"]');

  sections.forEach(initializeVideoShowcase);
}

function initializeStoryList(section) {
  const directContainers = Array.from(section.children).filter(
    (child) => child.getAttribute("handle") === "container",
  );
  const list =
    section.querySelector(':scope > [handle="container2"]') ||
    directContainers.find((container) =>
      container.querySelector(':scope > [handle="container"]'),
    );

  if (!list || initializedStoryLists.has(list)) return;

  const existingTrack = list.querySelector(":scope > .wd030-story-track");
  const cardParent = existingTrack || list;
  const cards = Array.from(cardParent.children).filter(
    (child) =>
      child.getAttribute("handle") === "container" &&
      child.dataset.wd030StoryClone !== "true",
  );

  if (!cards.length) return;

  const scrollSpeed = 80;
  const hoverScrollSpeed = 36;
  let track = existingTrack;
  let cloneCards = [];
  let trackAnimation = null;
  let activeStateTimer = 0;
  let resizeFrame = 0;
  let lastListWidth = 0;
  let activeCard = null;
  let isPointerInside = false;

  const getTrackCards = () => [...cards, ...cloneCards];

  const mountTrack = () => {
    if (track) {
      track
        .querySelectorAll('[data-wd030-story-clone="true"]')
        .forEach((clone) => clone.remove());
      cards.forEach((card) => track.append(card));
    } else {
      track = document.createElement("div");
      track.className = "wd030-story-track";
      list.insertBefore(track, cards[0]);
      cards.forEach((card) => track.append(card));
    }
    track.style.removeProperty("transform");

    cloneCards = cards.map((card) => {
      const clone = card.cloneNode(true);
      clone.removeAttribute("id");
      clone
        .querySelectorAll("[id]")
        .forEach((child) => child.removeAttribute("id"));
      clone.dataset.wd030StoryClone = "true";
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("inert", "");
      track.append(clone);
      return clone;
    });
  };

  const setActiveCard = (nextCard) => {
    const nextIndex = nextCard
      ? getTrackCards().indexOf(nextCard) % cards.length
      : -1;
    const nextOriginal = nextIndex >= 0 ? cards[nextIndex] : null;

    if (activeCard === nextOriginal) return;

    getTrackCards().forEach((card, index) => {
      card.classList.toggle(
        "is-active",
        nextIndex >= 0 && index % cards.length === nextIndex,
      );
    });
    activeCard = nextOriginal;
  };

  const stopCarousel = () => {
    trackAnimation?.cancel();
    trackAnimation = null;
    window.clearInterval(activeStateTimer);
    activeStateTimer = 0;
    list.classList.remove("is-story-rotating");
  };

  const setCarouselSpeed = (speed) => {
    if (!trackAnimation) return;

    const rate = speed / scrollSpeed;
    if (typeof trackAnimation.updatePlaybackRate === "function") {
      trackAnimation.updatePlaybackRate(rate);
    } else {
      trackAnimation.playbackRate = rate;
    }
  };

  const syncActiveCard = () => {
    if (!track) return;

    const listBounds = list.getBoundingClientRect();
    const cardBounds = getTrackCards().map((card) => ({
      card,
      bounds: card.getBoundingClientRect(),
    }));
    const nextCard =
      cardBounds.find(
        ({ bounds }) =>
          bounds.top >= listBounds.top - 0.5 &&
          bounds.bottom <= listBounds.bottom + 0.5,
      )?.card ||
      cardBounds.find(
        ({ bounds }) =>
          bounds.top >= listBounds.top && bounds.top < listBounds.bottom,
      )?.card ||
      cardBounds.find(
        ({ bounds }) =>
          bounds.bottom > listBounds.top && bounds.top < listBounds.bottom,
      )?.card;

    setActiveCard(nextCard || null);
  };

  const startCarousel = (preserveProgress = false) => {
    const previousDuration = trackAnimation?.effect?.getTiming().duration;
    const previousTime = trackAnimation?.currentTime;
    const progress =
      preserveProgress && previousDuration && previousTime !== null
        ? (previousTime % previousDuration) / previousDuration
        : 0;
    stopCarousel();

    if (
      reducedMotionMedia.matches ||
      !track ||
      cards.length < 2 ||
      typeof track.animate !== "function"
    ) {
      syncActiveCard();
      return;
    }

    const cycleDistance =
      cloneCards[0].getBoundingClientRect().top -
      cards[0].getBoundingClientRect().top;
    if (!Number.isFinite(cycleDistance) || cycleDistance <= 0) return;

    const duration = (cycleDistance / scrollSpeed) * 1000;
    list.classList.add("is-story-rotating");
    trackAnimation = track.animate(
      [
        { transform: "translate3d(0, 0, 0)" },
        { transform: `translate3d(0, -${cycleDistance}px, 0)` },
      ],
      {
        duration,
        easing: "linear",
        iterations: Infinity,
      },
    );
    if (progress) trackAnimation.currentTime = progress * duration;
    setCarouselSpeed(isPointerInside ? hoverScrollSpeed : scrollSpeed);
    lastListWidth = list.getBoundingClientRect().width;
    syncActiveCard();
    activeStateTimer = window.setInterval(syncActiveCard, 100);
  };

  const syncResponsiveState = () => {
    stopCarousel();
    mountTrack();
    list.classList.add("wd030-story-list", "is-story-enhanced");
    setActiveCard(cards[0]);
    startCarousel();
  };

  list.addEventListener("pointerenter", () => {
    isPointerInside = true;
    setCarouselSpeed(hoverScrollSpeed);
  });
  list.addEventListener("pointerleave", () => {
    isPointerInside = false;
    setCarouselSpeed(scrollSpeed);
  });

  reducedMotionMedia.addEventListener("change", syncResponsiveState);
  desktopMedia.addEventListener("change", syncResponsiveState);
  window.addEventListener(
    "resize",
    () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        if (Math.abs(list.getBoundingClientRect().width - lastListWidth) > 0.5) {
          startCarousel(true);
        }
      });
    },
    { passive: true },
  );

  syncResponsiveState();
  initializedStoryLists.add(list);
}

function initializeSection6(root = document) {
  const sections = root.matches?.('[handle="section6"]')
    ? [root]
    : root.querySelectorAll('[handle="section6"]');

  sections.forEach(initializeStoryList);
}

function initializePage(root = document) {
  initializeSection3(root);
  initializeSection4(root);
  initializeSection5(root);
  initializeSection6(root);
  initializeSection7(root);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializePage(), {
    once: true,
  });
} else {
  initializePage();
}

document.addEventListener("shopify:section:load", (event) => {
  initializePage(event.target);
});
